import React from "react";
import { useTranslation } from "react-i18next";
import AccountabilityCard from "../../../../parts/cards/profile-accountability";
import submit from "submit-form";
import scriptLoader from "../../../../utils/scriptLoader";
import {
  compareArrays,
  filterAccountabilityAttrs,
  getAccountabilitiesList,
  isEditing,
  isItemFilled,
  isStateFilled,
} from "../../../../utils/daily-accountability";
import ToolTip from "../../../../parts/tooltip-portal";
import moment from "moment";
import ConfirmationModal from "../../../../parts/modals/ConfirmationModal";
import SvgIcon from "../../../../parts/SvgIcon";

const weekDays = ["mon", "tue", "wed", "thu", "fri", "sat", "sun"];
const Accountability = ({
  athlete,
  locals,
  userToLoadInformation,
  organizationCoach,
  organizationOwner,
}) => {
  let days = {};
  weekDays.forEach((day) => (days = { ...days, [day]: false }));
  const newItem = {
    title: "",
    description: "",
    days,
    editing: true,
    adding: true,
  };
  const { t } = useTranslation(["athlete-portal", "common"]);
  const { query, origin, activeUser } = locals;
  // Always the logged-in coach's own preference — never the (possibly different)
  // organization coach's, even when viewing that coach's athlete.
  const startingCalendarDay = activeUser?.startingCalendarDay ?? 0;
  const { athleteSwitch } = query;
  const referAsClients = activeUser?.referAsClients || false;
  const roleLabel = referAsClients
    ? t("programs.client")
    : t("programs.athlete");
  const { customAccountability } = athlete;

  const [state, setState] = React.useState(false);
  const [changed, setChanged] = React.useState(false);
  const [adding, setAdding] = React.useState(false);
  const [canSubmit, setCanSubmit] = React.useState(false);

  const [changeOrder, setChangeOrder] = React.useState(false);
  const [orderedIds, setOrderedIds] = React.useState([]);
  const [editState, setEditState] = React.useState(false);
  const [idToDelete, setIdToDelete] = React.useState(undefined); //this is to save the id to delete, and also open/close the delete modal
  const [refresh, setRefresh] = React.useState(false);
  const [override, setOverride] = React.useState(false);
  // function to reorder the list of accountabilities
  const saveReorder = () => {
    if (orderedIds.length > 0) {
      let orderedItems = [...state];
      orderedItems.sort((a, b) => {
        const indexA = orderedIds.indexOf(a._id);
        const indexB = orderedIds.indexOf(b._id);
        return indexA - indexB;
      });
      submit(
        `/actions/daily-accountability/add-athlete-accountability/${athlete._id}`,
        {
          method: "POST",
          body: {
            list: orderedItems,
            userToRequest: userToLoadInformation._id,
          },
        }
      );
      setState(orderedItems);
    }

    setChangeOrder(false);
  };

  const cancelReorder = () => {
    setChangeOrder(false);
    setOrderedIds([]);
    setRefresh(!refresh);
  };

  const addItem = () => {
    setAdding(true);
    const newState = state.map((item) => {
      return { ...item, editing: false };
    });
    setState([...newState, newItem]);
  };
  const cancelChanges = () => {
    setEditState(false);
    setAdding(false);
    setState(
      // In case of an organization, the userToLoadInformation should be the organization owner,
      // cause templates are stored at that level
      getAccountabilitiesList(
        organizationOwner || userToLoadInformation,
        athlete
      )
    );
  };
  const handleEditing = (i) => {
    setState(
      state.map((item, index) => {
        return i === index
          ? { ...item, editing: !item.editing }
          : { ...item, editing: false };
      })
    );
  };
  const saveChanges = () => {
    setEditState(false);
    return submit(
      `/actions/daily-accountability/add-athlete-accountability/${athlete._id}${
        athleteSwitch ? "?athleteSwitch=true" : ""
      }`,
      {
        method: "POST",
        body: {
          list: state.map((item) => filterAccountabilityAttrs(item)),
          userToRequest: userToLoadInformation._id,
        },
      }
    );
  };
  const removeItem = (id) => {
    setIdToDelete(id); // opening the modal sending the id to delete
  };

  const handleCancelDelete = () => {
    setIdToDelete(undefined);
  };

  const handleConfirmDelete = () => {
    const newState = state.filter((item, i) => i !== idToDelete);
    setState(newState);
    if (newState.length === 0) {
      submit(
        `/actions/daily-accountability/remove-accountabilities/${athlete._id}${
          athleteSwitch ? "?athleteSwitch=true" : ""
        }`,
        {
          method: "POST",
          body: { userToRequest: userToLoadInformation._id },
        }
      );
    } else {
      submit(
        `/actions/daily-accountability/update-athlete-accountability/${
          athlete._id
        }${athleteSwitch ? "?athleteSwitch=true" : ""}`,
        {
          method: "POST",
          body: {
            list: newState.map((item) => filterAccountabilityAttrs(item)),
            userToRequest: userToLoadInformation._id,
          },
        }
      );
    }
    setIdToDelete(undefined);
  };
  const handleOverride = () => {
    setState([]);
    setOverride(false);
    submit(
      `/actions/daily-accountability/remove-accountabilities/${athlete._id}${
        athleteSwitch ? "?athleteSwitch=true" : ""
      }`,
      {
        method: "POST",
        body: { userToRequest: userToLoadInformation._id },
      }
    );
  };
  const athleteAccountabilities = React.useMemo(() => {
    // In case of an organization, the userToLoadInformation should be the organization owner,
    // cause templates are stored at that level
    return getAccountabilitiesList(
      organizationOwner || userToLoadInformation,
      athlete
    );
  }, [userToLoadInformation, athlete]);

  React.useEffect(() => {
    compareArrays(athleteAccountabilities, state)
      ? setChanged(true)
      : setChanged(false);
    if (
      (adding && isItemFilled(state[state.length - 1])) ||
      isStateFilled(state)
    )
      setCanSubmit(true);
    else setCanSubmit(false);
    if (isEditing(state)) setEditState(true);
    else setEditState(false);
  }, [athlete, state]);

  React.useEffect(() => {
    setState(athleteAccountabilities);
  }, [athlete]);
  React.useEffect(() => {
    if (document.querySelector(".principal-grabber-div")) {
      scriptLoader(
        "https://cdn.jsdelivr.net/npm/sortablejs@1.15.7/Sortable.min.js",
        () => {
          const dragImage = document.createElement("img");
          dragImage.src =
            "data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7";
          const sortable = new Sortable(
            document.querySelector(".principal-grabber-div"),
            {
              animation: 350,
              handle: ".drag-handle",
              setData(dataTransfer) {
                dataTransfer.setDragImage(dragImage, 0, 0);
              },
              onEnd: (e) => {
                if (e.oldIndex === e.newIndex) return;

                const newOrderedIds = Array.from(
                  document.querySelectorAll(".item-grabber-container")
                ).map((item) => item.id);

                setOrderedIds(newOrderedIds);
              },
            }
          );
        }
      );
    }
  }, []);
  return (
    <div role="accountability-wrapper">
      <header>
        <div>
          <div>
            <h4>{t("accountability.title")}</h4>
            {/* tooltip for mobile */}
            <ToolTip
              id={"accountability-tooltip"}
              body={t("accountability.tooltipBody")}
              childrenContainerClass="tooltip-coach"
            >
              <SvgIcon
                alt="info icon"
                src="/img/new-icons/question-circle.svg"
              />
            </ToolTip>
          </div>

          <div>
            <p className="p5">
              {t("accountability.description", { role: roleLabel })}
            </p>
          </div>
        </div>
        {state?.length === 0 ? (
          <hgroup>
            <a
              role="button"
              data-button-type="secondary"
              href="/actions/refresh?modal[type]=accountability-templates"
            >
              {t("accountability.template")}
            </a>
          </hgroup>
        ) : (
          <hgroup>
            {!changeOrder && !customAccountability && (
              <button
                data-button-type="secondary"
                onClick={
                  !editState
                    ? () => {
                        setChangeOrder(!changeOrder);
                      }
                    : null
                }
              >
                {t("accountability.changeOrder")}
              </button>
            )}
            {changeOrder && !customAccountability && (
              <button
                onClick={cancelReorder}
                data-button-type="secondary"
                data-action="cancel"
              >
                {t("common:cancel")}
              </button>
            )}
            {changeOrder && !customAccountability && (
              <button
                onClick={saveReorder}
                data-button-type="primary"
                disabled={orderedIds.length === 0}
              >
                {t("common:save")}
              </button>
            )}
            {!changeOrder && !customAccountability && (
              <a
                role="button"
                data-button-type="secondary"
                href="/actions/refresh?modal[type]=accountability-templates"
              >
                {t("accountability.template")}
              </a>
            )}
            {!changeOrder && (
              <a
                data-button-type="primary"
                role="button"
                href={
                  organizationCoach
                    ? `/athlete-portal/metrics/accountability?date=${moment().format(
                        "YYYYMMDD"
                      )}&athlete=${
                        athlete._id
                      }&organizationCoach=${organizationCoach}`
                    : `/athlete-portal/metrics/accountability?date=${moment().format(
                        "YYYYMMDD"
                      )}&athlete=${athlete._id}`
                }
              >
                {t("accountability.viewReport")}
              </a>
            )}
          </hgroup>
        )}
      </header>
      <ul>
        <div className="principal-grabber-div">
          {!customAccountability &&
            state?.length > 0 &&
            state.map((item, i) => (
              <AccountabilityCard
                hey
                key={`${item._id}${refresh}`}
                propState={athlete.accountability?.accountabilities || []}
                athleteId={athlete._id}
                state={state}
                id={i}
                setState={setState}
                changeOrder={changeOrder}
                editing={item.editing || false}
                adding={item.adding || false}
                handleEditing={handleEditing}
                removeItem={removeItem}
                startingCalendarDay={startingCalendarDay}
              />
            ))}
          <ConfirmationModal
            isOpen={idToDelete !== undefined}
            title={t("accountability.deleteTitle")}
            contentText={t("accountability.deleteConfirm")}
            cancelButtonText={t("common:cancel")}
            cancelButtonAction={handleCancelDelete}
            confirmButtonText={t("common:delete")}
            confirmButtonAction={handleConfirmDelete}
            icon="delete"
          />
        </div>
      </ul>
      {!customAccountability && state?.length > 0 && (
        <footer>
          {canSubmit && !changeOrder && !changed && (
            <button data-button-type="outline" onClick={addItem}>
              {t("common:add")}
            </button>
          )}
          <div>
            {(changed || editState) && (
              <button
                onClick={cancelChanges}
                data-button-type="secondary"
                data-action="cancel"
              >
                {t("common:cancel")}
              </button>
            )}
            {changed && canSubmit && (
              <button
                onClick={canSubmit ? saveChanges : null}
                data-button-type="primary"
                // className={canSubmit ? "acc-btn save" : "acc-btn "}
              >
                {t("common:save")}
              </button>
            )}
          </div>
        </footer>
      )}

      {(state?.length === 0 || customAccountability) && (
        <div className="no-accountabilities-assigned">
          <h4>
            {customAccountability
              ? t("accountability.customTitle")
              : t("accountability.noTitle")}
          </h4>
          <p className="p5">
            {customAccountability
              ? t("accountability.customDescription", { role: roleLabel })
              : t("accountability.noDescription")}
          </p>

          {customAccountability ? (
            <div>
              <button
                data-button-type="secondary"
                onClick={() => setOverride(true)}
              >
                {t("accountability.overrideButton")}
              </button>
              <ConfirmationModal
                isOpen={override}
                title={t("accountability.overrideTitle")}
                contentText={t("accountability.overrideConfirm", {
                  role: roleLabel,
                })}
                cancelButtonText={t("common:cancel")}
                cancelButtonAction={() => setOverride(false)}
                confirmButtonText={t("accountability.override")}
                confirmButtonAction={handleOverride}
                icon="delete"
              />
            </div>
          ) : (
            <div>
              <a
                role="button"
                data-button-type="primary"
                href="/actions/refresh?modal[type]=accountability-templates"
              >
                {t("accountability.assignTemplate")}
              </a>
              <button data-button-type="secondary" onClick={addItem}>
                {t("accountability.createNew")}
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default Accountability;
